import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useJoinPublicVideoRoom, useJoinVideoRoom } from "services/video";
import { VideoRoom, VideoRoom_State } from "protogen/video_pb";
import { Note } from "protogen/notes_pb";
import { Suggestions } from "protogen/suggestions_pb";
import { FamilyStub } from "protogen/common_pb";
import { AccountStub } from "protogen/common_pb";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdvisor } from "common/userUtils";
import { LocalUserChoices } from "@livekit/components-react";
import VideoCallAdvisor from "components/video/VideoCallAdvisor";
import VideoCallMember from "components/video/VideoCallMember";
import { useVideoCall } from "components/hooks/useVideoCall";

const VideoCall: React.FC = () => {
  const currentUser = useContext(CurrentUserContext);
  const isAdvisor = isUserAdvisor(currentUser);
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const [videoRoom, setVideoRoom] = useState<VideoRoom | null>(null);
  const [note, setNote] = useState<Note | null>(null);
  const [participants, setParticipants] = useState<AccountStub[]>([]);
  const [token, setToken] = useState<string>("");
  const [family, setFamily] = useState<FamilyStub | undefined>(undefined);
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [userChoices, setUserChoices] = useState<LocalUserChoices | null>(null);
  const [userClickedJoin, setUserClickedJoin] = useState<boolean>(false);
  const joinVideoRequestFn = currentUser.ref
    ? useJoinVideoRoom
    : useJoinPublicVideoRoom;
  const { request: joinVideoRoom } = joinVideoRequestFn();
  const { startVideoCall, videoCallState, resetManuallyEnded } = useVideoCall();

  const fetchAndJoinVideoRoom = useCallback(
    async (hostRejoin: boolean = false) => {
      if (!uuid) return;

      resetManuallyEnded();

      const response = await joinVideoRoom({ uuid, hostRejoin });
      const room = response?.videoRoom || null;

      setVideoRoom(room);
      setNote(response?.note || null);
      setParticipants([
        ...(response?.host ? [response.host] : []),
        ...(response?.participants || []),
      ]);
      setToken(response?.token || "");
      setFamily(response?.family);
      setSuggestions(response?.suggestions || null);
    },
    [uuid, joinVideoRoom, resetManuallyEnded],
  );

  const isCallActive =
    videoRoom?.state === VideoRoom_State.ACTIVE &&
    videoCallState.activeVideoRoom !== null &&
    !videoCallState.manuallyEnded;

  useEffect(() => {
    if (videoRoom && token && !videoCallState.manuallyEnded) {
      if (videoRoom.state !== VideoRoom_State.ACTIVE) {
        return;
      }

      if (!userClickedJoin) {
        return;
      }

      const currentActiveRoom = videoCallState.activeVideoRoom;
      if (!currentActiveRoom || currentActiveRoom.ref !== videoRoom.ref) {
        startVideoCall(videoRoom, token, userChoices || undefined);
      }
    }
  }, [
    videoRoom,
    token,
    startVideoCall,
    videoCallState.activeVideoRoom,
    videoCallState.manuallyEnded,
    userChoices,
    userClickedJoin,
  ]);

  const handleReJoin = (choices?: LocalUserChoices) => {
    if (choices) {
      setUserChoices(choices);
    }
    setUserClickedJoin(true);
    resetManuallyEnded();
    fetchAndJoinVideoRoom(true);
  };

  const handleVideoSetupJoin = (choices: LocalUserChoices) => {
    setUserChoices(choices);
    setUserClickedJoin(true);
    fetchAndJoinVideoRoom(true);
  };

  useEffect(() => {
    fetchAndJoinVideoRoom();
  }, []);

  if (!uuid) {
    navigate("/video");
    return null;
  }

  return isAdvisor ? (
    <VideoCallAdvisor
      videoRoom={videoRoom}
      participants={participants}
      note={note}
      family={family}
      isCallActive={isCallActive && !videoCallState.isPipMode}
      onReJoin={handleReJoin}
      suggestions={suggestions}
      onRefreshSuggestions={fetchAndJoinVideoRoom}
      onVideoSetupJoin={handleVideoSetupJoin}
      token={token}
    />
  ) : (
    <VideoCallMember
      isCallActive={isCallActive && !videoCallState.isPipMode}
      onReJoin={handleReJoin}
    />
  );
};

export default VideoCall;
